<template>
    <div class="nba-trace">
        <div v-for="(step, index) in data" :key="index" class="step-container">
            <div class="line-gutter">
                <div class="line-path q-mt-xs" v-if="index < data.length - 1 || isExpanded(index)"></div>
                <div class="icon-node" :class="{ done: isFinalStep(index) }"></div>
            </div>

            <div class="step-content">
                <div class="step-header" @click="toggle(index)" :style="`padding-bottom: ${isExpanded(index) || index === data.length - 1 ? '0' : '10'}px;`">
                    <div class="flex items-center">
                        <span class="step-name">{{ step.step_name }}</span>
                        <q-icon v-if="detailsOf(step).length" name="expand_more" size="14px" class="q-ml-xs step-chevron" :class="{ 'rotate180': isExpanded(index) }"/>
                    </div>
                </div>

                <div v-if="isExpanded(index)" class="details-section" :style="`padding-bottom: ${index < data.length - 1 ? '6' : '0'}px;`">
                    <div v-for="(detail, detailIndex) in detailsOf(step)" :key="detailIndex" class="detail-item">
                        <div class="detail-icon-node"></div>
                        <div class="detail-content">
                            <div v-for="(line, lineIndex) in String(detail).split('\n')" :key="lineIndex" v-html="markdownToHtml(line)" class="detail-text"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <q-spinner-dots v-if="running" size="20px" color="secondary" class="q-ml-xs q-mt-xs"/>
        <div v-else-if="!data.length" class="empty-trace">The agent did not record a thought trace for this run.</div>
    </div>
</template>

<script>
export default {
    name: "nba-thought-trace",

    props: {
        // The steps the agent has written to BCO_MASTER.THOUGHT_TRACE so far. The parent polls for
        // them, so the array grows while a run is in flight and this only ever renders what it is
        // given — there is no reveal animation to play.
        data: {
            type: Array,
            required: true,
            default: () => [],
        },
        running: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            // Steps read top to bottom as the run progresses, so they start open and this only
            // holds the ones the user has since folded away.
            collapsedSteps: {},
        };
    },

    methods: {
        detailsOf(step) {
            return Array.isArray(step?.details) ? step.details : [];
        },

        isExpanded(index) {
            return Boolean(this.detailsOf(this.data[index]).length) && !this.collapsedSteps[index];
        },

        isFinalStep(index) {
            return !this.running && index === this.data.length - 1;
        },

        toggle(index) {
            if (!this.detailsOf(this.data[index]).length) {
                return;
            }
            this.collapsedSteps = { ...this.collapsedSteps, [index]: !this.collapsedSteps[index] };
        },

        markdownToHtml(markdown) {
            markdown = markdown.replace(/\*\*(.*?)\*\*/g, '<b style="font-weight: 600; font-size: 11px;">$1</b>');
            markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<i>$1</i>');
            markdown = markdown.replace(/^- (.*$)/gm, '<li style="margin-left:14px; font-size: 10px; line-height: 16px;">$1</li>');
            return markdown;
        },
    },
};
</script>

<style scoped>
.nba-trace {
    width: 100%;
    min-width: 0;
}

.empty-trace {
    font-size: 11px;
    color: #5f5f5f;
}

.step-container {
    display: flex;
    position: relative;
    width: 100%;
    min-width: 0;
}

.line-gutter {
    flex-shrink: 0;
    width: 16px;
    position: relative;
    display: flex;
    justify-content: center;
}

.line-path {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background-image: linear-gradient(to bottom, #f3b699 40%, transparent 0%);
    background-size: 1px 6px;
    background-repeat: repeat-y;
}

.icon-node {
    width: 7px;
    height: 7px;
    background-color: #ff4e00;
    border-radius: 50%;
    margin-top: 4px;
    z-index: 1;
}

.icon-node.done {
    background-color: #2e7d46;
}

.step-content {
    flex-grow: 1;
    min-width: 0;
}

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    min-height: 16px;
}

.step-name {
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.step-header:hover .step-name,
.step-header:hover .step-chevron {
    color: #1f1f1f;
}

.step-chevron {
    color: #757575;
    transition: transform 0.15s ease;
}

.rotate180 {
    transform: rotate(180deg);
}

.details-section {
    margin-top: 4px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-left: 2px;
    margin-top: 6px;
    min-width: 0;
}

.detail-icon-node {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    background-color: #f5966e;
    border-radius: 50%;
    margin-right: 8px;
    margin-top: 5px;
}

.detail-content {
    min-width: 0;
    line-height: 1.5;
    color: #5f5f5f;
}

.detail-text {
    font-size: 10px;
    line-height: 16px;
    overflow-wrap: anywhere;
}
</style>
